import React from 'react';
import { motion } from 'framer-motion';
import ScrollHeader from '../components/ScrollHeader';
import ImageModal from '../components/ImageModal';

const CloudAnalyzer: React.FC = () => {
  const [selectedImage, setSelectedImage] = React.useState<string | null>(null);

  return (
    <div className="max-w-4xl mx-auto">
      <ScrollHeader title="Cloud Analyzer" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="prose prose-lg dark:prose-invert max-w-none">
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            Analyze your existing cloud infrastructure, get optimization recommendations, and export infrastructure to code.
          </p>

          <div className="bg-primary-500/10 dark:bg-primary-500/20 border-l-4 border-primary-500 p-6 rounded-r-lg mb-8">
            <h3 className="text-primary-600 dark:text-primary-400 mt-0 mb-2">💡 Key Concept</h3>
            <p className="mb-0">
              Cloud Analyzer connects to your existing cloud infrastructure, analyzes it for optimization 
              opportunities, security issues, and cost savings. It can also export your current infrastructure 
              to infrastructure-as-code format.
            </p>
          </div>

          <h2>Overview</h2>
          <p>
            The Cloud Analyzer is a powerful tool that helps you understand and optimize your existing cloud 
            infrastructure. It scans your cloud resources, provides actionable recommendations, and can even 
            convert your existing infrastructure into code.
          </p>

          <div 
            className="my-8 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-700 cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors"
            onClick={() => setSelectedImage('/screenshots/cloud_analyzer/cloud_analyzer.png')}
          >
            <img 
              src="/screenshots/cloud_analyzer/cloud_analyzer.png" 
              alt="Cloud Analyzer Interface" 
              className="w-full"
            />
          </div>

          <h2>Key Features</h2>

          <h3>Infrastructure Analysis</h3>
          <p>
            Cloud Analyzer scans your cloud environment and provides insights on:
          </p>
          <ul>
            <li><strong>Resource Inventory:</strong> Complete list of all cloud resources</li>
            <li><strong>Cost Analysis:</strong> Breakdown of spending by service and resource</li>
            <li><strong>Security Posture:</strong> Identification of security vulnerabilities</li>
            <li><strong>Compliance Status:</strong> Check against industry standards and best practices</li>
            <li><strong>Performance Metrics:</strong> Resource utilization and efficiency</li>
          </ul>

          <h3>Optimization Recommendations</h3>
          <p>
            Based on the analysis, Cloud Analyzer provides actionable recommendations:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
            <div className="bg-light-surface dark:bg-dark-surface p-6 rounded-lg border border-light-border dark:border-dark-border">
              <h4 className="text-primary-500 dark:text-primary-400 mt-0">Cost Optimization</h4>
              <ul className="mb-0 text-sm">
                <li>Identify underutilized resources</li>
                <li>Suggest right-sizing opportunities</li>
                <li>Recommend reserved instances</li>
                <li>Find unused resources to delete</li>
              </ul>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-6 rounded-lg border border-light-border dark:border-dark-border">
              <h4 className="text-primary-500 dark:text-primary-400 mt-0">Security Improvements</h4>
              <ul className="mb-0 text-sm">
                <li>Detect open security groups</li>
                <li>Identify unencrypted resources</li>
                <li>Flag overly permissive IAM policies</li>
                <li>Recommend security best practices</li>
              </ul>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-6 rounded-lg border border-light-border dark:border-dark-border">
              <h4 className="text-primary-500 dark:text-primary-400 mt-0">Performance Tuning</h4>
              <ul className="mb-0 text-sm">
                <li>Optimize instance types</li>
                <li>Improve network configurations</li>
                <li>Enhance database performance</li>
                <li>Reduce latency issues</li>
              </ul>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-6 rounded-lg border border-light-border dark:border-dark-border">
              <h4 className="text-primary-500 dark:text-primary-400 mt-0">Compliance</h4>
              <ul className="mb-0 text-sm">
                <li>Check against CIS benchmarks</li>
                <li>Verify HIPAA compliance</li>
                <li>Ensure PCI-DSS requirements</li>
                <li>Validate SOC 2 controls</li>
              </ul>
            </div>
          </div>

          <h3>Export Infrastructure to Code</h3>
          <p>
            One of the most powerful features is the ability to export your existing infrastructure to code:
          </p>

          <div 
            className="my-8 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-700 cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors"
            onClick={() => setSelectedImage('/screenshots/cloud_analyzer/export cloud to code.png')}
          >
            <img 
              src="/screenshots/cloud_analyzer/export cloud to code.png" 
              alt="Export Infrastructure to Code" 
              className="w-full"
            />
          </div>

          <p>
            The Export feature allows you to:
          </p>
          <ul>
            <li>Convert existing cloud resources to Terraform code</li>
            <li>Generate infrastructure-as-code for your current setup</li>
            <li>Create a baseline for future infrastructure management</li>
            <li>Enable version control for your infrastructure</li>
            <li>Facilitate disaster recovery and replication</li>
          </ul>

          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-6 rounded-r-lg mb-6">
            <h4 className="text-blue-800 dark:text-blue-400 mt-0 mb-2">📤 Export to Code</h4>
            <p className="mb-0 text-blue-700 dark:text-blue-300">
              Click the "Export" button in Cloud Analyzer to convert your existing infrastructure into 
              Terraform code. This is particularly useful for bringing legacy infrastructure under 
              infrastructure-as-code management.
            </p>
          </div>

          <h2>How to Use Cloud Analyzer</h2>

          <h3>1. Connect Your Cloud Account</h3>
          <p>
            Before using Cloud Analyzer, ensure you have set up cloud integrations:
          </p>
          <ul>
            <li>Navigate to the Integrations page</li>
            <li>Configure your cloud provider credentials</li>
            <li>Grant necessary read permissions for analysis</li>
          </ul>

          <h3>2. Run Analysis</h3>
          <p>
            Initiate a scan of your cloud infrastructure:
          </p>
          <ol>
            <li>Select the cloud provider and region to analyze</li>
            <li>Choose the scope (entire account or specific resources)</li>
            <li>Click "Analyze" to start the scan</li>
            <li>Wait for the analysis to complete (typically 1-5 minutes)</li>
          </ol>

          <h3>3. Review Recommendations</h3>
          <p>
            Once the analysis is complete, review the recommendations:
          </p>
          <ul>
            <li>Prioritized list of issues and opportunities</li>
            <li>Estimated cost savings for each recommendation</li>
            <li>Security risk levels (Critical, High, Medium, Low)</li>
            <li>Implementation difficulty and impact</li>
          </ul>

          <h3>4. Export to Code (Optional)</h3>
          <p>
            If you want to manage your infrastructure as code:
          </p>
          <ol>
            <li>Click the "Export" button</li>
            <li>Select the resources to export</li>
            <li>Choose the output format (Terraform, CloudFormation, etc.)</li>
            <li>Download or save to a new project</li>
          </ol>

          <h2>Analysis Categories</h2>

          <h3>Cost Optimization</h3>
          <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg mb-6">
            <h4 className="text-gray-700 dark:text-gray-300 mt-0 mb-4">Common Findings:</h4>
            <ul className="space-y-2 mb-0">
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Idle Resources:</strong> EC2 instances with low CPU utilization
              </li>
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Unattached Volumes:</strong> EBS volumes not attached to any instance
              </li>
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Old Snapshots:</strong> Snapshots that can be deleted
              </li>
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Reserved Instance Opportunities:</strong> Workloads suitable for RIs
              </li>
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Storage Class Optimization:</strong> S3 objects that should use cheaper storage tiers
              </li>
            </ul>
          </div>

          <h3>Security Analysis</h3>
          <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg mb-6">
            <h4 className="text-gray-700 dark:text-gray-300 mt-0 mb-4">Security Checks:</h4>
            <ul className="space-y-2 mb-0">
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Open Security Groups:</strong> Ports exposed to the internet
              </li>
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Unencrypted Data:</strong> Databases and storage without encryption
              </li>
              <li className="text-gray-600 dark:text-gray-400">
                <strong>IAM Issues:</strong> Overly permissive policies and unused credentials
              </li>
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Public Buckets:</strong> S3 buckets with public access
              </li>
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Missing MFA:</strong> Accounts without multi-factor authentication
              </li>
            </ul>
          </div>

          <h3>Performance Optimization</h3>
          <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg mb-6">
            <h4 className="text-gray-700 dark:text-gray-300 mt-0 mb-4">Performance Insights:</h4>
            <ul className="space-y-2 mb-0">
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Instance Right-Sizing:</strong> Over or under-provisioned instances
              </li>
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Database Optimization:</strong> Query performance and indexing suggestions
              </li>
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Network Latency:</strong> Suboptimal routing or placement
              </li>
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Caching Opportunities:</strong> Resources that would benefit from caching
              </li>
            </ul>
          </div>

          <h2>Export to Code Feature</h2>

          <h3>What Gets Exported</h3>
          <p>
            When you export infrastructure to code, Cloud Analyzer generates:
          </p>
          <ul>
            <li><strong>Resource Definitions:</strong> Complete Terraform configurations for all resources</li>
            <li><strong>Variable Files:</strong> Parameterized values for easy customization</li>
            <li><strong>Module Structure:</strong> Organized, reusable module definitions</li>
            <li><strong>State Configuration:</strong> Backend configuration for state management</li>
            <li><strong>Documentation:</strong> README files explaining the infrastructure</li>
          </ul>

          <h3>Export Process</h3>
          <ol>
            <li>
              <strong>Select Resources:</strong> Choose which resources to export (or select all)
            </li>
            <li>
              <strong>Configure Options:</strong> Set output format and organization preferences
            </li>
            <li>
              <strong>Generate Code:</strong> Cloud Analyzer creates the infrastructure code
            </li>
            <li>
              <strong>Review Output:</strong> Examine the generated code in the Code Editor
            </li>
            <li>
              <strong>Save to Project:</strong> Add the code to an existing or new project
            </li>
          </ol>

          <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-6 rounded-r-lg mb-6">
            <h4 className="text-green-800 dark:text-green-400 mt-0 mb-2">🔄 Infrastructure as Code Migration</h4>
            <p className="mb-0 text-green-700 dark:text-green-300">
              Use the Export feature to migrate from manually created infrastructure to infrastructure-as-code. 
              This enables version control, reproducibility, and easier management of your cloud resources.
            </p>
          </div>

          <h2>Recommendation Priorities</h2>

          <p>
            Cloud Analyzer categorizes recommendations by priority:
          </p>

          <div className="space-y-4 my-6">
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded-r-lg">
              <h4 className="text-red-800 dark:text-red-400 mt-0 mb-2">🔴 Critical</h4>
              <p className="text-sm text-red-700 dark:text-red-300 mb-0">
                Security vulnerabilities or compliance violations that need immediate attention
              </p>
            </div>

            <div className="bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-500 p-4 rounded-r-lg">
              <h4 className="text-orange-800 dark:text-orange-400 mt-0 mb-2">🟠 High</h4>
              <p className="text-sm text-orange-700 dark:text-orange-300 mb-0">
                Significant cost savings or important security improvements
              </p>
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-4 rounded-r-lg">
              <h4 className="text-yellow-800 dark:text-yellow-400 mt-0 mb-2">🟡 Medium</h4>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-0">
                Moderate improvements to cost, security, or performance
              </p>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded-r-lg">
              <h4 className="text-blue-800 dark:text-blue-400 mt-0 mb-2">🔵 Low</h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-0">
                Minor optimizations and best practice suggestions
              </p>
            </div>
          </div>

          <h2>Supported Cloud Providers</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
            <div className="bg-light-surface dark:bg-dark-surface p-4 rounded-lg border border-light-border dark:border-dark-border">
              <h4 className="text-primary-500 dark:text-primary-400 mt-0 mb-2">Amazon Web Services</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-0">
                Full support for AWS services and resources
              </p>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-4 rounded-lg border border-light-border dark:border-dark-border">
              <h4 className="text-primary-500 dark:text-primary-400 mt-0 mb-2">Microsoft Azure</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-0">
                Comprehensive Azure resource analysis
              </p>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-4 rounded-lg border border-light-border dark:border-dark-border">
              <h4 className="text-primary-500 dark:text-primary-400 mt-0 mb-2">Google Cloud Platform</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-0">
                Complete GCP infrastructure scanning
              </p>
            </div>
          </div>

          <h2>Using Recommendations</h2>

          <h3>Implementing Changes</h3>
          <p>
            For each recommendation, you can:
          </p>
          <ul>
            <li><strong>View Details:</strong> See full explanation and impact analysis</li>
            <li><strong>Apply Automatically:</strong> Let Ana implement the change</li>
            <li><strong>Generate Code:</strong> Get infrastructure code for the recommended change</li>
            <li><strong>Dismiss:</strong> Mark as not applicable or already addressed</li>
            <li><strong>Schedule:</strong> Plan implementation for a later time</li>
          </ul>

          <h3>Tracking Progress</h3>
          <p>
            Cloud Analyzer helps you track optimization progress:
          </p>
          <ul>
            <li>Dashboard showing implemented vs pending recommendations</li>
            <li>Cost savings achieved over time</li>
            <li>Security posture improvements</li>
            <li>Compliance score trends</li>
          </ul>

          <h2>Best Practices</h2>

          <h3>Regular Analysis</h3>
          <p>
            Run Cloud Analyzer regularly to:
          </p>
          <ul>
            <li>Catch new optimization opportunities</li>
            <li>Identify configuration drift</li>
            <li>Monitor cost trends</li>
            <li>Ensure ongoing compliance</li>
          </ul>

          <h3>Prioritize Security</h3>
          <p>
            Always address critical and high-priority security recommendations first:
          </p>
          <ul>
            <li>Fix exposed resources immediately</li>
            <li>Enable encryption for sensitive data</li>
            <li>Review and tighten IAM policies</li>
            <li>Implement multi-factor authentication</li>
          </ul>

          <h3>Cost Management</h3>
          <p>
            Use Cloud Analyzer for proactive cost management:
          </p>
          <ul>
            <li>Set up regular analysis schedules</li>
            <li>Track cost trends over time</li>
            <li>Implement quick wins (delete unused resources)</li>
            <li>Plan larger optimizations (reserved instances, right-sizing)</li>
          </ul>

          <h2>Integration with Other Features</h2>

          <h3>Export to Projects</h3>
          <p>
            Exported infrastructure code can be saved to a project:
          </p>
          <ul>
            <li>Create a new project for the exported infrastructure</li>
            <li>View and edit the code in the Code Editor</li>
            <li>Visualize the infrastructure in the Visualisation tool</li>
            <li>Make modifications using Chat with Ana</li>
          </ul>

          <h3>Continuous Improvement</h3>
          <p>
            Use Cloud Analyzer in your infrastructure workflow:
          </p>
          <ol>
            <li>Deploy infrastructure using Ana</li>
            <li>Run Cloud Analyzer to check for issues</li>
            <li>Implement recommendations</li>
            <li>Re-analyze to verify improvements</li>
            <li>Repeat regularly for continuous optimization</li>
          </ol>

          <h2>Next Steps</h2>
          <p>
            After analyzing your infrastructure:
          </p>
          <ul>
            <li>
              <strong>Implement Recommendations:</strong> Address critical and high-priority items
            </li>
            <li>
              <strong>Export to Code:</strong> Convert infrastructure to IaC for better management
            </li>
            <li>
              <strong>Set Up Monitoring:</strong> Track metrics for recommended changes
            </li>
            <li>
              <strong>Schedule Regular Scans:</strong> Keep your infrastructure optimized over time
            </li>
          </ul>

          <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-6 rounded-r-lg mt-8">
            <h3 className="text-green-800 dark:text-green-400 mt-0 mb-2">✨ Pro Tip</h3>
            <p className="mb-0 text-green-700 dark:text-green-300">
              Run Cloud Analyzer monthly to catch cost creep and security drift early. Many organizations 
              save 20-40% on cloud costs by implementing the recommendations from regular analysis scans.
            </p>
          </div>
        </div>
      </motion.div>

      <ImageModal
        isOpen={selectedImage !== null}
        src={selectedImage || ''}
        onClose={() => setSelectedImage(null)}
        alt="Screenshot"

      />
    </div>
  );
};

export default CloudAnalyzer;
